import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '@/lib/i18n'
import {
	dashboardPercentage,
	formatDashboardDate,
	formatDashboardNumber,
	resolveDashboardLocale
} from '../-lib/dashboard-formatters'
import {
	dashboardAnalyticsSummaryQueryOptions,
	dashboardUsageQueryOptions
} from '../-lib/dashboard-queries'
import {
	DashboardQueryError,
	DashboardQueryLoading
} from './dashboard-query-state'

export function DashboardSummaryMetrics() {
	const summaryQuery = useQuery(dashboardAnalyticsSummaryQueryOptions())
	const { t, i18n } = useTranslation()
	const locale = resolveDashboardLocale(
		i18n.resolvedLanguage ?? i18n.language
	)

	if (summaryQuery.isPending) {
		return (
			<article className='portal-card col-span-full min-h-[190px] xl:col-span-3'>
				<DashboardQueryLoading
					label={t('common:dashboard.analytics_loading')}
				/>
			</article>
		)
	}

	if (summaryQuery.isError) {
		return (
			<article className='portal-card col-span-full min-h-[190px] xl:col-span-3'>
				<DashboardQueryError
					description={t(
						'common:dashboard.analytics_error_description'
					)}
					isRetrying={summaryQuery.isFetching}
					onRetry={() => summaryQuery.refetch()}
					retryLabel={t(
						summaryQuery.isFetching
							? 'common:dashboard.retrying'
							: 'common:dashboard.retry'
					)}
					title={t('common:dashboard.analytics_error_title')}
				/>
			</article>
		)
	}

	let latencyDetail = t('common:dashboard.latency_unavailable_description')
	if (summaryQuery.data.metrics.averageLatencyMs !== null) {
		latencyDetail =
			summaryQuery.data.metrics.p95LatencyMs === null
				? t('common:dashboard.p95_unavailable')
				: t('common:dashboard.p95_latency', {
						value: Math.round(
							summaryQuery.data.metrics.p95LatencyMs
						)
					})
	}

	return (
		<>
			<Metric
				detail={t('common:dashboard.since', {
					date: formatDashboardDate(
						summaryQuery.data.todayStartedAt,
						locale
					)
				})}
				title={t('common:dashboard.requests_today')}
				value={formatDashboardNumber(
					summaryQuery.data.metrics.total,
					locale
				)}
			/>
			<Metric
				detail={latencyDetail}
				title={t('common:dashboard.average_latency')}
				value={
					summaryQuery.data.metrics.averageLatencyMs === null
						? 'N/A'
						: t('common:dashboard.latency_value', {
								value: Math.round(
									summaryQuery.data.metrics.averageLatencyMs
								)
							})
				}
			/>
			<Metric
				detail={t('common:dashboard.rate_limited', {
					value: formatDashboardNumber(
						summaryQuery.data.metrics.rateLimited,
						locale
					)
				})}
				title={t('common:dashboard.errors')}
				value={formatDashboardNumber(
					summaryQuery.data.metrics.clientErrors +
						summaryQuery.data.metrics.serverErrors,
					locale
				)}
			/>
		</>
	)
}

export function DashboardQuotaMetric() {
	const usageQuery = useQuery(dashboardUsageQueryOptions())
	const { t, i18n } = useTranslation()
	const locale = resolveDashboardLocale(
		i18n.resolvedLanguage ?? i18n.language
	)

	if (usageQuery.isPending) {
		return (
			<article className='portal-card min-h-[190px]'>
				<DashboardQueryLoading
					label={t('common:dashboard.usage_loading')}
				/>
			</article>
		)
	}

	if (usageQuery.isError) {
		return (
			<article className='portal-card min-h-[190px]'>
				<DashboardQueryError
					description={t('common:dashboard.usage_error_description')}
					isRetrying={usageQuery.isFetching}
					onRetry={() => usageQuery.refetch()}
					retryLabel={t(
						usageQuery.isFetching
							? 'common:dashboard.retrying'
							: 'common:dashboard.retry'
					)}
					title={t('common:dashboard.usage_error_title')}
				/>
			</article>
		)
	}

	const quotaPercent = dashboardPercentage(
		usageQuery.data.monthlyRequests.used,
		usageQuery.data.limits.monthlyRequestQuota
	)

	return (
		<Metric
			detail={t('common:dashboard.remaining', {
				value: formatDashboardNumber(
					usageQuery.data.monthlyRequests.remaining,
					locale
				)
			})}
			percentage={quotaPercent}
			title={t('common:dashboard.monthly_quota')}
			value={t('common:dashboard.quota_value', {
				limit: formatDashboardNumber(
					usageQuery.data.limits.monthlyRequestQuota,
					locale
				),
				used: formatDashboardNumber(
					usageQuery.data.monthlyRequests.used,
					locale
				)
			})}
		/>
	)
}

function Metric(props: {
	title: string
	value: string
	detail: string
	percentage?: number
}) {
	return (
		<article className='portal-card min-h-[190px]'>
			<div className='portal-card__header'>
				<h2 className='portal-card__title'>{props.title}</h2>
			</div>
			<p className='dashboard-metric__value'>{props.value}</p>
			<p className='dashboard-metric__label'>{props.detail}</p>
			{props.percentage === undefined ? null : (
				<div className='progress'>
					<span style={{ width: `${props.percentage}%` }} />
				</div>
			)}
		</article>
	)
}
